import React, { useMemo } from 'react';
import { Flex, Pagination, Radio, Typography } from 'antd';
import { SbTable, SbTag, SbTooltip } from 'uiCore';
import {
  EMPLOYEE_PAGE_SIZE_OPTIONS,
  TABLE_FILTER_OPTIONS,
} from 'constants/employeeManagement.constants';
import {
  EmployeeTableScrollAreaStyled,
  EmployeePaginationFooterStyled,
  IncompleteRowClassName,
  FitContentTagStyle,
} from './EmployeeManagementV2.styles';

const { Text } = Typography;

const EMPLOYEE_ROW_TOOLTIP =
  'Click to open the employee detail drawer and view or edit their details, profile assignment, and JD link.';

const TOOLTIP_EMPLOYEE_FILTER =
  'Filter employees by profile completion status: All employees, those with complete profiles, or those with incomplete/missing data.';

const EmployeeTableV2 = ({
  employees,
  pagination: paginationInfo,
  loading,
  statusFilter,
  onPageChange,
  onStatusFilterChange,
  onRowClick,
}) => {
  const columns = useMemo(
    () => [
      {
        title: 'Emp ID',
        key: 'emp_id',
        width: 120,
        render: (_, record) => record.employee_data?.employee_id || '-',
      },
      {
        title: 'Emp Email',
        key: 'emp_email',
        width: 240,
        render: (_, record) => record.employee_data?.employee_email_id || '-',
      },
      {
        title: 'First Name',
        key: 'first_name',
        width: 140,
        render: (_, record) => record.employee_data?.employee_first_name || '-',
      },
      {
        title: 'Last Name',
        key: 'last_name',
        width: 140,
        render: (_, record) => record.employee_data?.employee_last_name || '-',
      },
      {
        title: 'Role Title',
        key: 'role_title',
        width: 180,
        render: (_, record) =>
          record.employee_assigned_role_data?.role_title || '-',
      },
      {
        title: 'Role Position',
        key: 'role_position',
        width: 150,
        render: (_, record) => {
          const position =
            record.employee_assigned_role_data?.role_position || '-';
          const isIncomplete = !record.complete;
          return (
            <SbTag color={isIncomplete ? 'warning' : 'default'}>
              {position}
            </SbTag>
          );
        },
      },
      {
        title: 'Profile(s)',
        key: 'profiles',
        width: 200,
        render: (_, record) => {
          const roles = record.roles || [];
          return (
            <Flex vertical gap={4}>
              {roles.map(({ role_name, role_id }) => (
                <SbTag
                  key={role_id}
                  color={!record.complete ? 'warning' : 'default'}
                  style={FitContentTagStyle}
                >
                  {role_name}
                </SbTag>
              ))}
            </Flex>
          );
        },
      },
      {
        title: 'Reporting Mgr Name',
        key: 'reporting_mgr_name',
        width: 180,
        render: (_, record) => {
          const mgr = record.reporting_manager_data || {};
          const name =
            `${mgr.reporting_manager_first_name || ''} ${mgr.reporting_manager_last_name || ''}`.trim();
          return name || '-';
        },
      },
      {
        title: 'Rep Mgr Emp ID',
        key: 'rep_mgr_emp_id',
        width: 140,
        render: (_, record) =>
          record.reporting_manager_data?.reporting_manager_employee_id || '-',
      },
      {
        title: 'HRBP Name',
        key: 'hrbp_name',
        width: 160,
        render: (_, record) => {
          const hrbp = record.hrbp_data || {};
          const name =
            `${hrbp.hrbp_first_name || ''} ${hrbp.hrbp_last_name || ''}`.trim();
          return name || '-';
        },
      },
      {
        title: 'HRBP Emp ID',
        key: 'hrbp_emp_id',
        width: 130,
        render: (_, record) => record.hrbp_data?.hrbp_employee_id || '-',
      },
    ],
    [],
  );

  const handlePaginationChange = (page, size) => {
    onPageChange({ pageNo: page, pageSize: size });
  };

  return (
    <>
      {/* Scrollable table area — no built-in pagination */}
      <EmployeeTableScrollAreaStyled>
        <SbTable
          columns={columns}
          dataSource={employees}
          loading={loading}
          rowKey={(record) => record.employee_data?.employee_id || record.sb_user_unique_id}
          rowClassName={(record) => (!record.complete ? IncompleteRowClassName : '')}
          onRow={(record) => ({
            onClick: () => onRowClick(record),
            title: EMPLOYEE_ROW_TOOLTIP,
          })}
          scroll={{ x: 1800, y: 'calc(100vh - 260px)' }}
          pagination={false}
          autoFilters={false}
          size="middle"
        />
      </EmployeeTableScrollAreaStyled>

      {/* Fixed pagination footer — always at the bottom */}
      <EmployeePaginationFooterStyled>
        <Flex align="center" gap={16}>
          <Radio.Group
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            size="small"
          >
            {TABLE_FILTER_OPTIONS.map((opt) => (
              <SbTooltip
                key={String(opt.value)}
                content={TOOLTIP_EMPLOYEE_FILTER}
              >
                <Radio.Button value={opt.value}>{opt.label}</Radio.Button>
              </SbTooltip>
            ))}
          </Radio.Group>
          <Text type="secondary">{paginationInfo.totalRecords} employees</Text>
        </Flex>

        <Pagination
          current={paginationInfo.currentPage}
          pageSize={paginationInfo.pageSize}
          total={paginationInfo.totalRecords}
          showSizeChanger
          pageSizeOptions={EMPLOYEE_PAGE_SIZE_OPTIONS}
          onChange={handlePaginationChange}
          onShowSizeChange={handlePaginationChange}
          size="small"
        />
      </EmployeePaginationFooterStyled>
    </>
  );
};

export default EmployeeTableV2;
